import React, { useState, useCallback, useMemo, useRef } from "react";
import {
  IonModal,
  IonContent,
  IonIcon,
  IonButton,
  IonHeader,
  IonToolbar,
  IonTitle,
} from "@ionic/react";
import { closeOutline, navigateOutline } from "ionicons/icons";
import { Capacitor } from "@capacitor/core";
import { GoogleMap, useJsApiLoader, Marker, OverlayView } from "@react-google-maps/api";
import { environment } from "../environment/environment";

interface Place {
  name: string;
  vicinity: string;
  place_id?: string;
  lat?: number;
  lng?: number;
}

type MappablePlace = Place & { lat: number; lng: number };

interface Props {
  isOpen: boolean;
  places: Place[];
  userLocation: { lat: number; lng: number } | null;
  onClose: () => void;
}

const calmMapStyles: google.maps.MapTypeStyle[] = [
  {
    featureType: "all",
    elementType: "labels.icon",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "poi",
    elementType: "all",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "transit",
    elementType: "all",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#ffffff" }],
  },
  {
    featureType: "road",
    elementType: "geometry.stroke",
    stylers: [{ color: "#d7ded8" }],
  },
  {
    featureType: "road",
    elementType: "labels.text.fill",
    stylers: [{ color: "#7b8b84" }],
  },
  {
    featureType: "road",
    elementType: "labels.text.stroke",
    stylers: [{ color: "#ffffff" }],
  },
  {
    featureType: "administrative.locality",
    elementType: "labels.text.fill",
    stylers: [{ color: "#31564e" }],
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#cfe8f3" }],
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [{ color: "#6a8a99" }],
  },
];

const libraries: ("places")[] = ["places"];

const formatMarkerLabel = (name: string) => {
  const trimmedName = name.trim();
  return trimmedName.length > 22 ? `${trimmedName.slice(0, 21)}...` : trimmedName;
};

const HeaderCloseButton: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <IonButton className="map-header-close-button" fill="clear" slot="end" onClick={onClose}>
    <IonIcon icon={closeOutline} />
  </IonButton>
);

const getOverlayOffset = (width: number, height: number) => ({
  x: -(width / 2),
  y: -(height + 42),
});

const MapModal: React.FC<Props> = ({ isOpen, places, userLocation, onClose }) => {
  const [selectedPlace, setSelectedPlace] = useState<MappablePlace | null>(null);
  const [isPresented, setIsPresented] = useState(false);
  const isInteractingWithPlaceCardRef = useRef(false);

  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: environment.GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const mapContainerStyle = {
    width: "100%",
    height: "100%",
  };

  // Keep the map centered on the recommended places. User location can be far
  // away in browsers/emulators, so it should not influence the map viewport.
  const visiblePlaces = useMemo(
    () =>
      places
        .map((place) => ({
          ...place,
          lat: Number(place.lat),
          lng: Number(place.lng),
        }))
        .filter(
          (place): place is MappablePlace =>
            Number.isFinite(place.lat) && Number.isFinite(place.lng)
        ),
    [places]
  );

  const getCenter = useCallback(() => {
    const allLocations = visiblePlaces.map(p => ({ lat: p.lat, lng: p.lng }));

    if (allLocations.length === 0) {
      // Default to Bacolod City center
      return { lat: 10.6765, lng: 122.9509 };
    }

    const avgLat = allLocations.reduce((sum, loc) => sum + loc.lat, 0) / allLocations.length;
    const avgLng = allLocations.reduce((sum, loc) => sum + loc.lng, 0) / allLocations.length;

    return { lat: avgLat, lng: avgLng };
  }, [visiblePlaces]);

  const fitMapToPlaces = useCallback(
    (map: google.maps.Map) => {
      if (visiblePlaces.length === 0) {
        map.setCenter(getCenter());
        map.setZoom(13);
        return;
      }

      const bounds = new google.maps.LatLngBounds();
      visiblePlaces.forEach((place) => bounds.extend({ lat: place.lat, lng: place.lng }));

      if (visiblePlaces.length === 1) {
        map.setCenter({ lat: visiblePlaces[0].lat, lng: visiblePlaces[0].lng });
        map.setZoom(15);
        return;
      }

      map.fitBounds(bounds, 72);
    },
    [getCenter, visiblePlaces]
  );

  const openDirections = useCallback(
    (place: MappablePlace) => {
      const params = new URLSearchParams({
        api: "1",
        travelmode: "driving",
      });

      if (userLocation) {
        params.set("origin", `${userLocation.lat},${userLocation.lng}`);
      }

      params.set("destination", `${place.lat},${place.lng}`);

      const url = `https://www.google.com/maps/dir/?${params.toString()}`;
      const target = Capacitor.isNativePlatform() ? "_system" : "_blank";
      window.open(url, target, "noopener,noreferrer");
    },
    [userLocation]
  );

  const markPlaceCardInteraction = useCallback(() => {
    isInteractingWithPlaceCardRef.current = true;
  }, []);

  const handleMapClick = useCallback(() => {
    if (isInteractingWithPlaceCardRef.current) {
      isInteractingWithPlaceCardRef.current = false;
      return;
    }

    setSelectedPlace(null);
  }, []);

  if (loadError) {
    return (
      <IonModal
        isOpen={isOpen}
        onDidDismiss={onClose}
        className="map-modal"
      >
        <IonHeader className="map-modal-header">
          <IonToolbar>
            <IonTitle>Map</IonTitle>
            <HeaderCloseButton onClose={onClose} />
          </IonToolbar>
        </IonHeader>
        <IonContent className="map-status-content">
          <p>Failed to load Google Maps. Please check your internet connection and API key.</p>
        </IonContent>
      </IonModal>
    );
  }

  if (!isLoaded) {
    return (
      <IonModal
        isOpen={isOpen}
        onDidDismiss={onClose}
        className="map-modal"
      >
        <IonHeader className="map-modal-header">
          <IonToolbar>
            <IonTitle>Map</IonTitle>
            <HeaderCloseButton onClose={onClose} />
          </IonToolbar>
        </IonHeader>
        <IonContent className="map-status-content">
          <p>Loading Google Maps...</p>
        </IonContent>
      </IonModal>
    );
  }

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      onDidPresent={() => setIsPresented(true)}
      onWillDismiss={() => {
        setIsPresented(false);
        setSelectedPlace(null);
      }}
      className="map-modal"
    >
      <IonHeader className="map-modal-header">
        <IonToolbar>
          <IonTitle>Map</IonTitle>
          <HeaderCloseButton onClose={onClose} />
        </IonToolbar>
      </IonHeader>

      <IonContent>
        {isPresented && (
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={getCenter()}
            zoom={13}
            onLoad={(map) => {
              fitMapToPlaces(map);
              // The modal card animates/sizes in after the map mounts, so Google
              // Maps caches a stale size and shows blank tiles. A real window
              // resize event forces it to re-measure — pulse until tiles load
              // (the card can take a few seconds to settle), then stop. Don't
              // re-fit bounds on every pulse or the viewport keeps changing and
              // tiles never finish; fit once when tiles are in.
              let ticks = 0;
              const id = window.setInterval(() => {
                window.dispatchEvent(new Event("resize"));
                if (++ticks >= 12) window.clearInterval(id);
              }, 600);
              window.google?.maps?.event?.addListenerOnce(map, "tilesloaded", () => {
                window.clearInterval(id);
                fitMapToPlaces(map);
              });
            }}
            onClick={handleMapClick}
            options={{
              clickableIcons: false,
              fullscreenControl: false,
              mapTypeControl: false,
              streetViewControl: false,
              styles: calmMapStyles,
            }}
          >
          {/* User location marker */}
          {userLocation && (
            <Marker
              position={userLocation}
              icon={{
                url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(`
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" fill="#43ae91" stroke="white" stroke-width="2"/>
                    <circle cx="12" cy="12" r="4" fill="white"/>
                  </svg>
                `),
                scaledSize: new google.maps.Size(24, 24),
                anchor: new google.maps.Point(12, 12),
              }}
              title="Your Location"
            />
          )}

          {/* Place markers */}
          {visiblePlaces.map((place, index) => {
            return (
              <Marker
                key={place.place_id || index}
                position={{ lat: place.lat, lng: place.lng }}
                onClick={() => setSelectedPlace(place)}
                icon={{
                  url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(`
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#ed4b82" stroke="white" stroke-width="2"/>
                      <circle cx="12" cy="9" r="2.5" fill="white"/>
                    </svg>
                  `),
                  scaledSize: new google.maps.Size(32, 32),
                  anchor: new google.maps.Point(16, 32),
                  labelOrigin: new google.maps.Point(16, -10),
                }}
                label={{
                  text: formatMarkerLabel(place.name),
                  color: "#ffffff",
                  fontSize: "12px",
                  fontWeight: "700",
                  className: "map-place-label",
                }}
              />
            );
          })}

          {/* Place dialog */}
          {selectedPlace && Number.isFinite(selectedPlace.lat) && Number.isFinite(selectedPlace.lng) && (
            <OverlayView
              position={{ lat: selectedPlace.lat, lng: selectedPlace.lng }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              getPixelPositionOffset={getOverlayOffset}
            >
              <div
                className="map-place-card"
                onClick={(event) => event.stopPropagation()}
                onMouseDown={markPlaceCardInteraction}
                onPointerDown={markPlaceCardInteraction}
                onTouchStart={markPlaceCardInteraction}
              >
                <button
                  className="map-place-card-close"
                  type="button"
                  aria-label="Close place details"
                  onClick={() => setSelectedPlace(null)}
                >
                  <IonIcon icon={closeOutline} />
                </button>
                <h4>{selectedPlace.name}</h4>
                <p>{selectedPlace.vicinity || "Location details unavailable."}</p>
                <button
                  className="map-directions-button"
                  type="button"
                  onMouseDown={markPlaceCardInteraction}
                  onPointerDown={markPlaceCardInteraction}
                  onTouchStart={markPlaceCardInteraction}
                  onClick={(event) => {
                    event.stopPropagation();
                    markPlaceCardInteraction();
                    openDirections(selectedPlace);
                  }}
                >
                  <IonIcon icon={navigateOutline} />
                  Directions
                </button>
              </div>
            </OverlayView>
          )}
          </GoogleMap>
        )}
      </IonContent>
    </IonModal>
  );
};

export default MapModal;
